import React, { useState, useEffect, useRef } from "react";
import {
  FileText,
  Image as ImageIcon,
  Receipt,
  Droplets,
  Camera,
  Wrench,
  Mail,
  ClipboardCheck,
  Calendar as CalIcon,
  Sparkles,
  AlertTriangle,
  CheckCircle2,
  Mic,
  ArrowDown,
  Download,
} from "lucide-react";
import Logo from "@/components/Logo";

/**
 * HeroPhoneMockup
 *
 * Landing-page hero device frame that auto-rotates through six product
 * surfaces every 5 seconds. Pauses on hover. Tab pills sit inside the
 * phone screen so the rotation is understandable and tappable.
 *
 * The six tabs show off different parts of the app: the dashboard
 * (Tenancy), the document vault, the chronology, the AI chat (Compass),
 * the Deep Read forensic analysis, and a transcribed voice note. We
 * deliberately picked these six so a first-time visitor can see the full
 * range without leaving the landing page.
 *
 * Built as a CSS/SVG iPhone frame with no image assets, so it stays
 * sharp at any DPR and matches the brand palette automatically.
 */
const TABS = [
  { id: "tenancy", label: "Home" },
  { id: "vault", label: "Vault" },
  { id: "timeline", label: "Time" },
  { id: "compass", label: "Chat" },
  { id: "deepread", label: "Deep" },
  { id: "voice", label: "Voice" },
];

export default function HeroPhoneMockup() {
  const [active, setActive] = useState("tenancy");
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (paused) return;
    timer.current = setInterval(() => {
      setActive((cur) => {
        const idx = TABS.findIndex((t) => t.id === cur);
        return TABS[(idx + 1) % TABS.length].id;
      });
    }, 5000);
    return () => clearInterval(timer.current);
  }, [paused]);

  const activeIdx = TABS.findIndex((t) => t.id === active);

  return (
    <div className="w-full flex flex-col items-center" data-testid="hero-phone-mockup">
      <div
        className="relative mx-auto"
        style={{ width: "300px" }}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {/* Phone body */}
        <div
          className="relative bg-[#0D2B52] p-2.5"
          style={{
            borderRadius: "42px",
            boxShadow: "0 30px 60px -18px rgba(13, 43, 82, 0.4), 0 8px 16px rgba(0,0,0,0.06)",
          }}
        >
          {/* Screen */}
          <div
            className="bg-[#FAF9F6] overflow-hidden relative"
            style={{ borderRadius: "30px", height: "600px" }}
          >
            {/* Status bar */}
            <div className="flex items-center justify-between px-5 pt-3 pb-1.5">
              <span className="font-mono text-[11px] text-[#0D2B52] font-semibold">9:41</span>
              <div className="flex items-center gap-1 text-[#0D2B52]">
                <span className="font-mono text-[10px]">●●●○</span>
                <span className="font-mono text-[10px] ml-1">73%</span>
              </div>
            </div>

            {/* Dynamic island */}
            <div
              className="absolute top-2 left-1/2 -translate-x-1/2 bg-black rounded-full"
              style={{ width: "82px", height: "22px" }}
              aria-hidden="true"
            />

            {/* App header */}
            <div className="flex items-center gap-2 px-4 pt-3 pb-3 border-b border-[#E5E4DE]">
              <Logo size={22} />
              <div className="font-serif text-[15px] text-[#0D2B52]">TenantTrail</div>
              <div className="ml-auto font-mono text-[9px] uppercase tracking-[0.1em] text-[#4B5563]">QLD</div>
            </div>

            {/* Tab strip — segmented control. Active tab shows a 5-second
                progress bar so the auto-rotation feels intentional, not random. */}
            <style>{`@keyframes tt-tab-progress { from { transform: scaleX(0); } to { transform: scaleX(1); } }`}</style>
            <div
              className="flex gap-px mx-3 mt-3 bg-[#E5E4DE]/60 p-0.5"
              style={{ borderRadius: "8px" }}
              data-testid="hero-phone-tabs"
            >
              {TABS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setActive(t.id)}
                  className={`relative overflow-hidden flex-1 py-1.5 font-mono text-[9px] uppercase tracking-[0.04em] transition-colors ${
                    active === t.id
                      ? "bg-white text-[#0D2B52] shadow-sm font-semibold"
                      : "text-[#4B5563] hover:text-[#0D2B52]"
                  }`}
                  style={{ borderRadius: "6px" }}
                  data-testid={`hero-phone-tab-${t.id}`}
                >
                  {t.label}
                  {active === t.id && (
                    <span
                      key={active + (paused ? "-p" : "-r")}
                      aria-hidden="true"
                      data-testid="hero-phone-tab-progress"
                      className="absolute left-0 bottom-0 h-[2px] w-full bg-[#1B6C62] origin-left"
                      style={{
                        animation: "tt-tab-progress 5s linear forwards",
                        animationPlayState: paused ? "paused" : "running",
                      }}
                    />
                  )}
                </button>
              ))}
            </div>

            {/* Tab content area */}
            <div className="px-4 pt-3 pb-3" style={{ height: "460px", overflow: "hidden" }}>
              {active === "tenancy" && <TenancyScreen />}
              {active === "vault" && <VaultScreen />}
              {active === "timeline" && <TimelineScreen />}
              {active === "compass" && <CompassScreen />}
              {active === "deepread" && <DeepReadScreen />}
              {active === "voice" && <VoiceScreen />}
            </div>

            {/* "More inside" hint — tells the visitor there's more to swipe through */}
            <style>{`@keyframes tt-hint-pulse { 0%,100% { opacity: .5; transform: translateX(0) rotate(-90deg); } 50% { opacity: 1; transform: translateX(2px) rotate(-90deg); } }`}</style>
            <div
              className="absolute left-0 right-0 flex items-center justify-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.12em] text-[#4B5563] pointer-events-none"
              style={{ bottom: "18px" }}
            >
              <span>
                {activeIdx + 1} / {TABS.length}
              </span>
              <span className="text-[#1B6C62]">·</span>
              <span>tap a tab for more</span>
              <ArrowDown
                size={10}
                strokeWidth={1.75}
                className="text-[#1B6C62]"
                style={{ animation: paused ? "none" : "tt-hint-pulse 1.6s ease-in-out infinite" }}
                data-testid="hero-phone-hint-chevron"
              />
            </div>

            {/* Home indicator */}
            <div
              className="absolute bottom-1.5 left-1/2 -translate-x-1/2 bg-[#0D2B52]/30 rounded-full"
              style={{ width: "100px", height: "3px" }}
              aria-hidden="true"
            />
          </div>
        </div>

        {/* Floor shadow */}
        <div
          className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-[#0D2B52]/10 blur-md"
          style={{ width: "210px", height: "10px", borderRadius: "50%" }}
          aria-hidden="true"
        />
      </div>

      <p className="text-[12px] text-[#6B7280] mt-4 text-center font-mono">
        A sample case file. Yours will look just like this.
      </p>
    </div>
  );
}

/* ----- Inner screens ----- */

function TenancyScreen() {
  return (
    <div className="fade-in" data-testid="hero-phone-screen-tenancy">
      <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-[#1B6C62] mb-1 font-semibold">
        Your tenancy · all good
      </div>
      <div className="font-serif text-[17px] text-[#0D2B52] leading-tight">12 Marlow Cres</div>
      <div className="text-[12px] text-[#4B5563] mb-3">Brisbane · Bond $2,200</div>

      <div className="space-y-1.5 mb-2.5">
        <PhoneRow label="Lease ends" value="2026-04-30" />
        <PhoneRow label="Photos on file" value="38" />
        <PhoneRow label="Voice notes" value="6" />
        <PhoneRow label="Check-ins" value="5 of 12" />
        <PhoneRow label="Compass chats" value="14 / 100" emphasis />
      </div>

      <div className="bg-[#E6F5F2] border-l-2 border-[#1B6C62] px-2.5 py-2 mb-2">
        <div className="font-mono text-[9px] uppercase tracking-[0.1em] text-[#1B6C62] mb-1 font-semibold">Compass</div>
        <div className="text-[12px] text-[#1A1A1A] leading-snug">
          &ldquo;In QLD your landlord needs to give you two months&rsquo; notice before the lease ends. Want me to draft a polite reply?&rdquo;
        </div>
      </div>

      <div className="flex items-center justify-between px-2 py-1.5 bg-[#FAF6E8] border border-[#8A6D3B]/30">
        <div className="flex items-center gap-1.5">
          <AlertTriangle size={12} className="text-[#8A6D3B]" strokeWidth={2} />
          <span className="text-[12px] text-[#0D2B52]">Next action: rent review on 1 Jul</span>
        </div>
        <span className="font-mono text-[10px] text-[#8A6D3B] font-semibold">14d</span>
      </div>
    </div>
  );
}

function VaultScreen() {
  const docs = [
    { Icon: FileText, name: "Lease agreement.pdf", meta: "Signed 30 Apr 2024" },
    { Icon: Receipt, name: "Bond receipt RTA", meta: "Lodged · #B-77421" },
    { Icon: ImageIcon, name: "Entry condition photos", meta: "38 images · verified" },
    { Icon: Droplets, name: "Water usage invoice", meta: "Q3 · $84.20" },
    { Icon: Mail, name: "Repair request email", meta: "14 Feb 2026 · opened" },
    { Icon: ClipboardCheck, name: "Routine inspection", meta: "Aug 2025 · disagreed" },
  ];
  return (
    <div className="fade-in" data-testid="hero-phone-screen-vault">
      <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-[#1B6C62] mb-1 font-semibold">
        Vault · 38 photos · 12 docs
      </div>
      <div className="font-serif text-[16px] text-[#0D2B52] leading-tight mb-2">
        All your paperwork, organised.
      </div>

      {/* Deep Read flag callout — surfaces a finding from the lease */}
      <div className="mb-2 bg-[#FAF6E8] border-l-2 border-[#8A6D3B] px-2 py-1.5 flex items-start gap-1.5">
        <Sparkles size={12} strokeWidth={1.75} className="text-[#8A6D3B] mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[9px] uppercase tracking-[0.1em] text-[#8A6D3B] font-semibold">Deep Read</div>
          <div className="text-[11.5px] text-[#1A1A1A] leading-snug">
            2 clauses to watch in your lease
          </div>
        </div>
      </div>

      <ul className="space-y-1.5">
        {docs.map((d) => (
          <li key={d.name} className="flex items-start gap-2 pb-1.5 border-b border-[#E5E4DE] last:border-b-0">
            <div className="p-1 bg-[#1B6C62]/10 text-[#1B6C62] mt-0.5">
              <d.Icon size={12} strokeWidth={1.75} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[12px] text-[#1A1A1A] truncate">{d.name}</div>
              <div className="font-mono text-[10px] text-[#4B5563] truncate">{d.meta}</div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TimelineScreen() {
  const events = [
    { tag: "Today", title: "Compass drafted reply", body: "Re: lease end notice", Icon: Mail },
    { tag: "3 days ago", title: "Photos added", body: "Bathroom mould · 6 imgs", Icon: Camera },
    { tag: "1 wk ago", title: "Repair logged", body: "Heater · acknowledged", Icon: Wrench },
    { tag: "2 wks ago", title: "Check-in done", body: "All good", Icon: ClipboardCheck },
    { tag: "Aug 2025", title: "Routine inspection", body: "Disagreed on 2 items", Icon: CalIcon },
  ];
  return (
    <div className="fade-in relative" data-testid="hero-phone-screen-timeline">
      <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-[#1B6C62] mb-1 font-semibold">
        Timeline · tribunal-ready
      </div>
      <div className="font-serif text-[16px] text-[#0D2B52] leading-tight mb-2">
        Every event, in order.
      </div>

      <div className="absolute left-[14px] top-[64px] bottom-[48px] w-px bg-[#E5E4DE]" aria-hidden="true" />
      <ul className="space-y-1.5 mb-2">
        {events.map((e, i) => (
          <li key={i} className="flex items-start gap-2 relative">
            <div className="p-1 bg-[#FAF9F6] border border-[#E5E4DE] text-[#1B6C62] relative z-10 mt-0.5">
              <e.Icon size={12} strokeWidth={1.75} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-mono text-[9px] uppercase tracking-[0.1em] text-[#4B5563] font-semibold">
                {e.tag}
              </div>
              <div className="text-[12px] text-[#1A1A1A] leading-snug">{e.title}</div>
              <div className="text-[11px] text-[#4B5563] leading-snug truncate">{e.body}</div>
            </div>
          </li>
        ))}
      </ul>

      {/* Export bundle CTA */}
      <div className="bg-[#0D2B52] text-white px-2.5 py-2 flex items-center gap-2">
        <Download size={13} strokeWidth={1.75} className="text-[#9DD6CB] shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[11.5px] font-medium leading-tight">Export PDF evidence bundle</div>
          <div className="font-mono text-[10px] text-[#9DD6CB]">28 pages · ready in 4s</div>
        </div>
      </div>
    </div>
  );
}

function CompassScreen() {
  return (
    <div className="fade-in" data-testid="hero-phone-screen-compass">
      <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-[#1B6C62] mb-1 font-semibold">
        Compass · in your corner
      </div>
      <div className="font-serif text-[16px] text-[#0D2B52] leading-tight mb-3">
        Ask anything. Get a plan.
      </div>
      <div className="space-y-2">
        <div className="text-[12px] text-[#1A1A1A] bg-[#FAF9F6] border border-[#E5E4DE] px-2 py-1.5 max-w-[88%]">
          <div className="font-mono text-[9px] uppercase tracking-[0.1em] text-[#4B5563] mb-0.5 font-semibold">You</div>
          Agent says I owe $480 for carpet cleaning. Can they?
        </div>
        <div className="text-[12px] text-[#1A1A1A] bg-[#E6F5F2] border border-[#1B6C62]/30 border-l-2 border-l-[#1B6C62] px-2 py-1.5 max-w-[94%] ml-auto">
          <div className="font-mono text-[9px] uppercase tracking-[0.1em] text-[#1B6C62] mb-0.5 font-semibold">Compass</div>
          In QLD, pro cleaning can&rsquo;t be required unless it&rsquo;s in your agreement <em>and</em> there&rsquo;s damage beyond fair wear and tear. Your lease (clause 24) doesn&rsquo;t require it. Want me to draft a polite pushback?
        </div>
        <div className="text-[12px] text-[#1A1A1A] bg-[#FAF9F6] border border-[#E5E4DE] px-2 py-1.5 max-w-[88%]">
          <div className="font-mono text-[9px] uppercase tracking-[0.1em] text-[#4B5563] mb-0.5 font-semibold">You</div>
          Yes please. Keep it friendly.
        </div>
        <div className="text-[12px] text-[#1A1A1A] bg-[#E6F5F2] border border-[#1B6C62]/30 border-l-2 border-l-[#1B6C62] px-2 py-1.5 max-w-[94%] ml-auto">
          <div className="font-mono text-[9px] uppercase tracking-[0.1em] text-[#1B6C62] mb-0.5 font-semibold">Compass</div>
          Done. Draft saved to your Vault. Ready to send.
        </div>
      </div>
    </div>
  );
}

function DeepReadScreen() {
  return (
    <div className="fade-in" data-testid="hero-phone-screen-deepread">
      <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-[#8A6D3B] mb-1 flex items-center gap-1 font-semibold">
        <Sparkles size={11} strokeWidth={1.75} /> Deep Read · lease.pdf
      </div>
      <div className="font-serif text-[16px] text-[#0D2B52] leading-tight">
        Three things to watch
      </div>
      <div className="font-mono text-[10px] text-[#4B5563] mb-2">
        Scanned 18 pages in 12s
      </div>

      <div className="space-y-1.5">
        <FindingRow
          tone="warn"
          title="Pro carpet cleaning required"
          body="Clause 24. In QLD this is not enforceable without damage."
        />
        <FindingRow
          tone="warn"
          title="Rent increase every 6 months"
          body="Clause 11.2. Minimum interval in QLD is 12 months."
        />
        <FindingRow
          tone="ok"
          title="Bond lodged with RTA"
          body="Receipt #B-77421 matches lease amount."
        />
        <FindingRow
          tone="ok"
          title="Cooling-off rights stated"
          body="Standard QLD clause. Nothing unusual here."
        />
      </div>

      <div className="mt-2 bg-[#FAF6E8] border-l-2 border-[#8A6D3B] px-2 py-1.5">
        <div className="text-[11.5px] text-[#1A1A1A] leading-snug">
          Tap a flag, Compass will write you a polite challenge letter.
        </div>
      </div>
    </div>
  );
}

function FindingRow({ tone, title, body }) {
  const accent = tone === "warn" ? "#8A6D3B" : "#1B6C62";
  const bg = tone === "warn" ? "#FAF6E8" : "#E6F5F2";
  const Icon = tone === "warn" ? AlertTriangle : CheckCircle2;
  return (
    <div className="bg-white border border-[#E5E4DE] px-2 py-1.5 flex items-start gap-1.5">
      <div
        className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5"
        style={{ background: bg }}
      >
        <Icon size={11} strokeWidth={2} style={{ color: accent }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[12px] text-[#0D2B52] font-medium leading-tight">{title}</div>
        <div className="text-[10.5px] text-[#4B5563] leading-snug">{body}</div>
      </div>
    </div>
  );
}

function VoiceScreen() {
  return (
    <div className="fade-in" data-testid="hero-phone-screen-voice">
      <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-[#1B6C62] mb-1 flex items-center gap-1 font-semibold">
        <Mic size={11} strokeWidth={1.75} /> Voice note · 2 minutes ago
      </div>
      <div className="font-serif text-[16px] text-[#0D2B52] leading-tight">
        Recorded after the inspection
      </div>
      <div className="font-mono text-[10px] text-[#4B5563] mb-2.5">
        Whisper transcript · 1:24
      </div>

      {/* Waveform */}
      <div className="bg-white border border-[#E5E4DE] px-2.5 py-2.5 flex items-end gap-0.5 h-14 mb-2">
        {[3, 6, 8, 5, 10, 12, 8, 14, 10, 6, 12, 8, 4, 9, 11, 7, 5, 10, 14, 11, 8, 4, 7, 9, 6, 11, 9, 5, 12, 8].map(
          (h, i) => (
            <div
              key={i}
              className="flex-1 bg-[#1B6C62]"
              style={{ height: `${h * 2.8}px`, opacity: 0.4 + (h / 14) * 0.6 }}
            />
          )
        )}
      </div>

      {/* Transcript */}
      <div className="bg-[#E6F5F2] border-l-2 border-[#1B6C62] px-2 py-1.5 mb-2">
        <div className="font-mono text-[9px] uppercase tracking-[0.1em] text-[#1B6C62] mb-0.5 font-semibold">
          Transcript
        </div>
        <div className="text-[11.5px] text-[#1A1A1A] leading-snug">
          Agent showed up at 4:15pm, ten minutes early. Took photos of the bathroom but skipped the back room. Said the mould was &ldquo;cosmetic&rdquo;.
        </div>
      </div>

      <div className="flex items-center gap-1.5 mb-1.5">
        <CheckCircle2 size={12} className="text-[#1B6C62]" strokeWidth={2} />
        <div className="font-mono text-[10.5px] text-[#4B5563]">
          Saved · timestamped · on your timeline
        </div>
      </div>
      <div className="bg-[#FAF6E8] border-l-2 border-[#8A6D3B] px-2 py-1.5">
        <div className="font-mono text-[9px] uppercase tracking-[0.1em] text-[#8A6D3B] mb-0.5 font-semibold">
          Compass
        </div>
        <div className="text-[11.5px] text-[#1A1A1A] leading-snug">
          Want me to turn this into a clear, dated statement for tribunal?
        </div>
      </div>
    </div>
  );
}

function PhoneRow({ label, value, emphasis = false }) {
  return (
    <div className="flex justify-between pb-1 border-b border-[#E5E4DE] last:border-b-0">
      <span className="text-[12px] text-[#4B5563]">{label}</span>
      <span className={`font-mono text-[12px] ${emphasis ? "text-[#1B6C62] font-semibold" : "text-[#1A1A1A]"}`}>
        {value}
      </span>
    </div>
  );
}
